import { useState, useRef } from 'react';
import { ArrowLeft, Camera, Paperclip, X, Send, Loader } from 'lucide-react';
import html2canvas from 'html2canvas-pro';
import { useAuth } from '../../App';
import { debugCollector } from '../../utils/DebugCollector';
import {
  createStationFeedback,
  createAdminFeedback,
  addStationFeedbackComment,
  addFeedbackComment,
  type FeedbackCategory,
} from '../../api/client';

const CATEGORY_OPTIONS: { value: FeedbackCategory; label: string }[] = [
  { value: 'BUG', label: 'Fehler' },
  { value: 'FEATURE', label: 'Verbesserung' },
  { value: 'OTHER', label: 'Sonstiges' },
];

interface NewTicketFormProps {
  onBack: () => void;
  onCreated: () => void;
}

export default function NewTicketForm({ onBack, onCreated }: NewTicketFormProps) {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<FeedbackCategory>('BUG');
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState('');
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isStation = !!user?.stationId;
  const canSubmit = title.trim().length >= 5 && !submitting;

  const handleScreenshot = async () => {
    if (capturing) return;
    setCapturing(true);

    const drawer = document.querySelector('[data-feedback-drawer]') as HTMLElement | null;
    try {
      // Ghost-Mode: Drawer verstecken
      if (drawer) {
        drawer.style.visibility = 'hidden';
        await new Promise((r) => setTimeout(r, 150));
      }

      const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        allowTaint: false,
        scale: 1,
      });

      // Drawer sofort wieder zeigen
      if (drawer) drawer.style.visibility = 'visible';

      canvas.toBlob((blob: Blob | null) => {
        if (!blob) {
          console.error('Canvas-Export fehlgeschlagen: Blob ist null.');
          setCapturing(false);
          return;
        }
        const file = new File([blob], `screen_${Date.now()}.jpg`, { type: 'image/jpeg' });
        setAttachments((prev) => [...prev, file]);
        setCapturing(false);
      }, 'image/jpeg', 0.8);
    } catch (error: any) {
      console.error('Screenshot fehlgeschlagen:', error);
      if (drawer) drawer.style.visibility = 'visible';
      setCapturing(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit || !user) return;
    setSubmitting(true);
    setError('');
    try {
      const debugData = JSON.stringify(debugCollector.getSnapshot());
      const total = Math.max(1, attachments.length);
      if (total > 1) setSubmitProgress(`Sende 1 von ${total}...`);

      const data = {
        title: title.trim(),
        category,
        description: message.trim() || undefined,
        pageUrl: window.location.pathname + window.location.search,
        image: attachments[0] || undefined,
        debugData,
      };

      let created;
      if (isStation && user.stationId) {
        created = await createStationFeedback(user.stationId, data);
      } else {
        created = await createAdminFeedback(data);
      }

      for (let i = 1; i < attachments.length; i++) {
        setSubmitProgress(`Sende ${i + 1} von ${total}...`);
        if (isStation && user.stationId) {
          await addStationFeedbackComment(user.stationId, created.id, {
            image: attachments[i],
          });
        } else {
          await addFeedbackComment(created.id, {
            image: attachments[i],
          });
        }
      }

      onCreated();
    } catch (e: any) {
      setError(e?.data?.error || 'Fehler beim Erstellen');
    } finally {
      setSubmitting(false);
      setSubmitProgress('');
    }
  };

  return (
    <div className="flex flex-col h-full" data-feedback-drawer>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b bg-slate-50 shrink-0">
        <button onClick={onBack} className="p-1 text-slate-500 hover:text-slate-700 rounded transition-colors">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h2 className="font-semibold text-slate-800">Neue Meldung</h2>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {/* Title */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Betreff</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Kurze Beschreibung (min. 5 Zeichen)"
            maxLength={100}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 placeholder:text-slate-400"
            autoFocus
          />
          {title.length > 0 && title.trim().length < 5 && (
            <p className="text-[11px] text-red-500 mt-1">Mindestens 5 Zeichen</p>
          )}
        </div>

        {/* Category */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Kategorie</label>
          <div className="flex gap-2">
            {CATEGORY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setCategory(opt.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  category === opt.value
                    ? 'bg-slate-800 text-white border-slate-800'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Message */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Nachricht (optional)</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Beschreibe das Problem oder deinen Vorschlag..."
            rows={4}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-slate-300 placeholder:text-slate-400"
          />
        </div>

        {/* Image */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">Bild / Screenshot</label>
          <div className="flex gap-2">
            <button
              onClick={handleScreenshot}
              disabled={capturing}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
            >
              {capturing ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
              {capturing ? 'Laden...' : 'Screenshot'}
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
            >
              <Paperclip className="w-3.5 h-3.5" />
              Datei
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) setAttachments((prev) => [...prev, f]);
                e.target.value = '';
              }}
            />
          </div>
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {attachments.map((file, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded-md">
                  <div className="relative w-10 h-10 shrink-0">
                    <img
                      src={URL.createObjectURL(file)}
                      alt="Preview"
                      className="w-full h-full object-cover rounded bg-white"
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] text-gray-500 truncate max-w-[100px]">{file.name}</span>
                    <button
                      onClick={() => setAttachments((prev) => prev.filter((_, i) => i !== idx))}
                      className="text-[10px] text-red-500 hover:underline text-left"
                    >
                      Entfernen
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
        )}
      </div>

      {/* Submit */}
      <div className="px-4 py-3 border-t shrink-0">
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-slate-800 text-white rounded-lg text-sm font-medium hover:bg-slate-700 transition-colors disabled:opacity-40 disabled:hover:bg-slate-800"
        >
          <Send className="w-4 h-4" />
          {submitting ? (submitProgress || 'Wird gesendet...') : 'Meldung absenden'}
        </button>
      </div>
    </div>
  );
}
